import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate } from "@remix-run/react";
import { FullWidthLayout } from './Player.styles';
import { PlayerBackButton } from './PlayerBackButton';
import { ScrollForMoreButton } from './ScrollForMoreButton';
import { usePlayerControlsVisible } from './use-player-events';
import { useSearchParams } from '../../../../../play-core/hooks/use-search-params';
import { toSupportedLanguage } from '../../../../../play-core/models/SupportedLanguage';
import { parseIntOrUndefined } from '../../../../../play-core/utils/number-helper';
import { VideoPlayer } from '../../../components/VideoPlayer/VideoPlayer';
import { usePath, useVideoConfig } from '../../../config/VideoAppContext';
import { useLatestVideos } from '../../../hooks/use-latest-videos';
import { getSearchParam } from '../../../utils/search-param-helper';
import { useFallbackPlaylist } from '../use-fallback-playlist';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const autoplay = {
  default: true,
  storage: true
};
const viewHeight = 100;
const screenHeightHeader = {
  default: 0,
  mediaQuery: [['small', 0]]
};
export const FullscreenPlayer = ({
  videoDetail,
  isInitialLocation
}) => {
  const size = {
    screenHeightHeader,
    viewHeight
  };
  const imageUrl = videoDetail?.imageUrl;
  const {
    defaultTextTrackLanguage
  } = useVideoConfig();
  const {
    videoDetailPath
  } = usePath();
  const navigate = useNavigate();
  const {
    startTime: seekPosition,
    urn = '',
    subtitleSize,
    subtitle
  } = useSearchParams();
  const startTime = parseIntOrUndefined(seekPosition);
  const [playbackUrn, setPlaybackUrn] = useState(urn);
  const [showBack, handlers] = usePlayerControlsVisible();
  const [playerIsFullHeight, setPlayerIsFullHeight] = useState(false);
  const ref = React.useRef(null);
  const handleResize = () => {
    if (ref.current) {
      setPlayerIsFullHeight(ref.current.clientHeight === window.innerHeight);
    }
  };
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();
  }, []);
  useEffect(() => {
    if (urn && playbackUrn !== urn) {
      setPlaybackUrn(urn);
    }
  }, [playbackUrn, urn]);
  const latestUnwatchedVideos = useLatestVideos(videoDetail);
  const fallbackPlaylist = useFallbackPlaylist(videoDetail);
  const endScreenPlaylist = latestUnwatchedVideos.length > 0 ? latestUnwatchedVideos : fallbackPlaylist;
  useEffect(() => {
    if (videoDetail) {
      const newRoute = videoDetailPath(videoDetail, {
        startTime
      });
      const location = window.location.href;
      if (!location.includes(newRoute) && location.includes(videoDetail.urn)) {
        // replace to titled route
        navigate(newRoute, {
          replace: true
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoDetail, videoDetailPath]);
  const updateUrl = nextUrn => {
    if (getSearchParam(window.location.search, 'urn') !== nextUrn) {
      const newRoute = videoDetailPath({
        urn: nextUrn
      });
      navigate(newRoute);
    }
  };
  const content = /*#__PURE__*/_jsx(VideoPlayer, {
    urn: playbackUrn,
    endScreenPlaylist: endScreenPlaylist,
    hasSegments: videoDetail?.hasSegments,
    playerFocus: true,
    size: size,
    seekPosition: startTime,
    textTrackForcedLanguage: toSupportedLanguage(subtitle),
    textTrackDefaultLanguage: defaultTextTrackLanguage,
    textTrackSizeLevel: parseIntOrUndefined(subtitleSize),
    onSegmentSwitched: nextUrn => {
      if (videoDetail?.isSegment) {
        updateUrl(nextUrn);
      }
    },
    onRecommendationHit: updateUrl,
    onContinuousPlaybackHit: updateUrl,
    showContinuousPlayback: true,
    autoPlay: autoplay,
    placeholderImage: imageUrl,
    youthProtectionColor: videoDetail?.youthProtectionColor,
    ...handlers
  });
  return /*#__PURE__*/_jsxs(FullWidthLayout, {
    ref: ref,
    children: [content, /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(PlayerBackButton, {
        visible: showBack,
        isInitialLocation: isInitialLocation
      }), playerIsFullHeight && /*#__PURE__*/_jsx(ScrollForMoreButton, {
        hasSegments: videoDetail?.hasSegments,
        visible: showBack
      })]
    })]
  });
};